import React, { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { CTASection } from '../components/CTASection';
import { DifferentialCard } from '../components/DifferentialCard';
import { differentials, siteConfig, heroImages } from '../data/mock';
import { Award, Users, Clock, Target } from 'lucide-react';

export const Sobre = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO 
        title="Sobre a Box7go - Estética Automotiva e Lava Jato Premium em Goiânia | Nossa História"
        description="Conheça a Box7go: lava jato e estética automotiva premium em Goiânia. Galpão coberto, produtos profissionais e equipe especializada. Tempo de pit stop, brilho de pódio."
        keywords="sobre box7go, lava jato goiania, estética automotiva, história box7go, quem somos"
      />

      {/* Hero */}
      <section className="relative min-h-[400px] flex items-center bg-black">
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImages[1]})`,
            opacity: 0.4
          }}
        ></div>
        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Sobre a Box7go
            </h1>
            <p className="text-xl text-white/90">
              Tempo de pit stop. Brilho de pódio.
            </p>
          </div>
        </div>
      </section>

      {/* Nossa História */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Quem Somos
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                A <strong>Box7go</strong> nasceu com o propósito de revolucionar o conceito de lava jato e estética automotiva em Goiânia e Aparecida de Goiânia. Combinamos a agilidade de um pit stop de Fórmula 1 com a excelência de acabamento que seu veículo merece.
              </p>
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                Nossa missão é oferecer desde a lavagem rápida e acessível até o detalhamento premium de alto padrão, sempre utilizando <strong>produtos automotivos profissionais</strong> e técnicas modernas de limpeza e proteção.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Trabalhamos em um <strong>galpão coberto</strong>, protegendo seu veículo do sol durante todo o processo, e contamos com uma <strong>equipe extremamente capacitada</strong> para entregar resultados impecáveis em cada serviço.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Nossos Valores
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00B4D8] rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-xl text-gray-900 mb-2">Excelência</h3>
              <p className="text-gray-600">
                Comprometimento com a qualidade premium em cada detalhe
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00B4D8] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-xl text-gray-900 mb-2">Profissionalismo</h3>
              <p className="text-gray-600">
                Equipe treinada e equipamentos de última geração
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00B4D8] rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-xl text-gray-900 mb-2">Agilidade</h3>
              <p className="text-gray-600">
                Rapidez sem comprometer a qualidade do resultado
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00B4D8] rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-xl text-gray-900 mb-2">Satisfação</h3>
              <p className="text-gray-600">
                Foco total na experiência e satisfação do cliente
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Nossos Diferenciais
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentials.map((differential, index) => (
              <DifferentialCard key={index} differential={differential} />
            ))}
          </div>
        </div>
      </section>

      {/* Compromisso */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Nosso Compromisso com Você
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Na Box7go, nos comprometemos a entregar mais do que um simples serviço de lavagem. Oferecemos uma <strong>experiência completa de cuidado automotivo</strong>, desde a limpeza rápida para o dia a dia até o acabamento premium com aspecto de limpo por até 30 dias, independentemente da cor do veículo.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Seja você alguém que busca praticidade e agilidade ou um entusiasta que quer o máximo em detalhamento, a Box7go tem a solução perfeita para seu veículo.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection 
        title="Venha conhecer a Box7go"
        subtitle="Agende uma visita ou solicite um orçamento pelo WhatsApp"
      />
    </>
  );
};
